import { action, observable, makeObservable } from 'mobx';
import { ChannelEditForm } from 'modules/channel/forms';
import { ChannelStore } from 'modules/channel/stores';
import { BaseViewStore } from 'core/stores';
import {
	translate,
	isSome,
	notificationError,
	isSuperAdminOrganization,
	formatDate,
	isCurrentDateGreater,
} from 'core/utils';
import { channelType, sourceOfChange, sellerCloudChannels } from 'common/constants';
import { ApplicationStore } from 'platform/application/stores';
import { LookupStore } from 'modules/lookup/stores';
import { CarrierStore } from 'modules/carrier/stores';
import { lookupRouteName, carrierChannelTypeEnum } from 'common/constants';
import { FormHelperService } from 'common/data';
import { ModalParams, ConfirmModalParams } from 'core/models';
import { BaasicDropdownViewStore } from 'common/stores';
import { localStorageHandler } from 'core/services';
import { CredentialsValidationStore } from 'modules/credentials-validation/stores';
import _ from 'lodash';

class ChannelEditViewStore extends BaseViewStore {
	@observable channelTypeDisabled = false;
	@observable timezoneOptions = [];
	@observable tempTimezoneOptions = [];
	@observable page = 1;
	@observable infiniteSearchPhrase = null;
	@observable isCarrierLoading = false;
	@observable isTimezoneLoading = false;
	@observable stateOptions = [];
	@observable loadingCarriers;
	@observable channelCarriers = [];
	@observable selectedCarriers = [];
	@observable scheduledRestockDate = null;
	carrierTypes = [];
	channelTypeOptions = [];
	convertWarehouses = [];
	warehouses = [];
	isEdit = false;
	debouncedTimezoneSearch = null;
	isProduction;
	channel;
	amazonOAuthStateHash = null;
	oAuthDate = null;
	form = new ChannelEditForm({
		onSuccess: async () => {
			await this.createOrUpdateChannel();
		},
	});

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.listStore = new ChannelStore(rootStore);
		this.credentialsValidationStore = new CredentialsValidationStore(rootStore);
		this.lookupStore = new LookupStore(rootStore);
		this.formHelperService = new FormHelperService();
		this.carrierStore = new CarrierStore(rootStore);
		this.applicationStore = new ApplicationStore(rootStore);
		this.isEdit = isSome(rootStore.routerStore.routerState.params.id);
		this.isSuperAdmin = isSuperAdminOrganization(rootStore);
		this.initialize();
	}

	@action.bound
	async initialize() {
		this.suspend();
		let tasks = [];
		tasks.push(this.getLookups());
		tasks.push(this.getWarehouses());
		this.isProduction = process.env.NODE_ENV == 'production';
		this.debouncedTimezoneSearch = _.debounce((searchPhrase, { action }) => {
			this.searchTimezoneAsync(searchPhrase, { action });
		}, 500);

		if (this.isSuperAdmin) {
			this.sellerDropdownStore = new BaasicDropdownViewStore({
				field: this.form.$('seller'),
				store: new ApplicationStore(this.rootStore),
				itemTemplate: {
					value: 'id',
					label: 'applicationTitle',
				},
				filterProperties: {
					sellerOnly: true,
				},
				onChangeCallback: this.clearData,
			});
			this.form.$('seller').set('rules', 'required');
		} else {
			tasks.push(this.getChannelCarriers());
		}
		await Promise.all(tasks);

		if (this.isEdit) {
			await this.getChannel();
			this.channelTypeDisabled = true;
			this.setFieldRules(this.form.$('channelType').value);
		} else {
			if (this.isSuperAdmin) {
				const seller = localStorageHandler.get('selectedSeller');
				if (seller) {
					this.sellerDropdownStore.setValue(seller);
					this.sellerId = this.sellerDropdownStore.value.value;
					await this.sellerDropdownStore.reload();
				}
			}
		}
		this.oAuthDate = new Date().toISOString();
		const md5 = require('md5');
		this.amazonOAuthStateHash = md5(this.rootStore.app.baasic.userHandler.user.user.id + this.oAuthDate);

		this.resume();
	}

	@action.bound
	validateCredentials = async () => {
		try {
			let data = this.form.values();
			await this.credentialsValidationStore.validateChannel({
				channelTypeName: data.channelType.label,
				...data,
			});
			this.rootStore.notificationStore.success(['MESSAGES.CHANNEL.CONNECTIONVALID']);
		} catch (err) {
			this.rootStore.notificationStore.error(['MESSAGES.CHANNEL.CONNECTIONINVALID']);
		}
	};

	@action.bound
	onCarrierChange = selectedValue => {
		this.selectedCarriers = selectedValue == null ? [] : selectedValue;
		this.form.$('carrierChannels').value = this.selectedCarriers;
		this.removeBuyShippingCarrier();
	};

	@action.bound
	clearData = async sellerDropdownValue => {
		await this.clearCarrierData(sellerDropdownValue);
		await this.getWarehouses();
	};

	@action.bound
	clearCarrierData = async sellerDropdownValue => {
		if (this.isEdit) {
			this.sellerId = this.channel.sellerId;
		} else {
			this.sellerId = sellerDropdownValue.value;
			this.clearBuyShippingCarrierFields();
			this.selectedCarriers = [];
			this.form.$('carrierChannels').clear();
		}
		await this.getChannelCarriers();
	};

	@action.bound
	getChannelCarriers = async () => {
		await this.carrierStore.find({
			rpp: 20,
			page: 1,
			sellerIds: this.sellerId ? [this.sellerId] : null,
		});
		this.channelCarriers = _.map(this.carrierStore.response.item, item => {
			return { value: item.id, label: item.name, extra: item };
		});
	};

	@action.bound
	getChannel = async () => {
		this.channel = await this.listStore.getChannel(this.rootStore.routerStore.routerState.params.id, {
			embed: [
				'channelType',
				'timezone',
				'carrier',
				'carrierChannels',
				'carrierChannels.carrier',
				'carrierChannels.carrier.carrierType',
				'productInventoryRestockDate',
				'customFieldInputs',
				'customFieldInputs.customFieldInputVariables',
				'customFieldInputs.customFieldInputVariables.customFieldVariable',
			],
		});
		this.selectedItem = this.channel;
		if (
			this.channel.productInventoryRestockDate &&
			this.channel.productInventoryRestockDate.restockDate != null &&
			!isCurrentDateGreater(this.channel.productInventoryRestockDate.restockDate)
		) {
			this.scheduledRestockDate = this.channel.productInventoryRestockDate.scheduledRestockDateTime;
			if (this.scheduledRestockDate != null) {
				this.scheduledRestockDate = formatDate(this.scheduledRestockDate, true, 'LLL');
			}
		}
		const { carrierChannels, ...other } = this.listStore.item; // dont update carrierChannels because it breaks multi-select validation
		this.formHelperService.updateForm(this.form, other, true);
		this.selectedCarriers = [];
		_.forEach(this.channel.carrierChannels, item => {
			if (item.carrierChannelType == carrierChannelTypeEnum.requiredCarrier) {
				this.selectedCarriers.push({ value: item.carrier.id, label: item.carrier.name, extra: item });
			}
		});
		this.form.$('carrierChannels').value = this.selectedCarriers;
		this.form.$('channelType').value = { value: this.channel.channelType.id, label: this.channel.channelType.name };
		this.form.$('timezone').value = { value: this.channel.timezone.id, label: this.channel.timezone.abrv };
		if (
			this.channel.channelType.name === channelType.amazon ||
			this.channel.channelType.name === channelType.amazonVendor ||
			this.channel.channelType.name === channelType.wooCommerce ||
			this.channel.channelType.name === channelType.walmart
		) {
			this.form.$('useConfirmShipment').value = this.channel.useConfirmShipment === 'true';
		}
		if (this.channel.channelType.name === channelType.sellerCloud) {
			this.form.$('channelId').value = _.find(sellerCloudChannels, sellerCloudChannel => {
				return sellerCloudChannel.value == this.channel.channelId;
			});
		}

		if (this.channel.channelType.name === channelType.amazonVendor) {
			this.form.$('useSandbox').value = this.channel.useSandbox === 'true'; // Because it comes as string from Json updateForm doesn't work.
		}

		if (this.isSuperAdmin) {
			this.sellerDropdownStore.setValue({ value: this.channel.sellerId });
			await this.sellerDropdownStore.reload();
		}
	};

	@action.bound
	onCheck(event, path) {
		this.form.$(path).value = event.currentTarget.checked;
	}

	@action.bound
	getWarehouses = async () => {
		this.warehouses = (await this.applicationStore.find({
			rpp: 100,
			page: 1,
			isOrganizationActive: true,
			warehouseOnly: true,
			sellerWarehousesOnlyOrganizationId: this.isSuperAdmin ? this.sellerId : null,
			includeOnlySellerWarehouses: true,
		})).item;
		await this.prepareData();
	};

	@action.bound
	prepareData = async () => {
		this.convertWarehouses = [];
		_.each(this.warehouses, warehouse => {
			this.convertWarehouses.push({
				warehouseName: warehouse.identifier,
				name: null, // Warehouse Code.
				warehouseId: warehouse.id,
			});
		});
	};

	@action.bound
	createOrUpdateChannel = async () => {
		this.suspend();
		try {
			let formValues = this.form.values();
			let carrierChannels = null;
			// For Amazon Vendor, there are no carriers.
			if (formValues.channelType.label !== channelType.amazonVendor) {
				carrierChannels = _.map(this.selectedCarriers, item => {
					return {
						carrierId: item.value,
						carrierChannelTypeEnum: carrierChannelTypeEnum.requiredCarrier,
					};
				});
				const buyShippingFields = _.filter(Object.getOwnPropertyNames(formValues), item => {
					return item.includes('BuyShippingCarrier');
				});
				_.forEach(buyShippingFields, fieldKey => {
					if (formValues[fieldKey]) {
						carrierChannels.push({
							carrierId: formValues[fieldKey].value,
							carrierChannelType: carrierChannelTypeEnum.buyShipping,
						});
					}
				});
			}
			let channelCredentials = {};

			if (formValues.channelType.label === channelType.walmart) {
				channelCredentials = {
					apiKey: formValues.apiKey,
					apiSecret: formValues.apiSecret,
					useConfirmShipment: true,
				};
			}

			if (
				formValues.channelType.label === channelType.amazon ||
				formValues.channelType.label === channelType.amazonVendor
			) {
				channelCredentials = {
					amazonSellerId: formValues.amazonSellerId,
					marketplaceId: formValues.marketplaceId,
					refreshToken: formValues.refreshToken,
					useConfirmShipment: true,
					sellingPartyId: formValues.sellingPartyId,
					useSandbox: formValues.useSandbox,
				};
			}

			if (formValues.channelType.label === channelType.wooCommerce) {
				channelCredentials = {
					url: formValues.url,
					consumerKey: formValues.consumerKey,
					consumerSecret: formValues.consumerSecret,
					useConfirmShipment: true,
				};
			}

			if (formValues.channelType.label === channelType.mirakl) {
				channelCredentials = {
					url: formValues.url,
					shopId: formValues.shopId,
					apiKey: formValues.apiKey,
				};
			}

			if (formValues.channelType.label === channelType.shopify) {
				channelCredentials = {
					url: formValues.url,
					accessToken: formValues.accessToken,
				};
			}

			if (formValues.channelType.label === channelType.sellerCloud) {
				channelCredentials = {
					serverId: formValues.serverId,
					userName: formValues.userName,
					password: formValues.password,
					channelId: formValues.channelId.value,
				};
			}

			if (formValues.channelType.label === channelType.costco) {
				channelCredentials = {
					apiKey: formValues.apiKey,
					partnerId: formValues.partnerId,
				};
			}

			if (formValues.channelType.label === channelType.logicBroker) {
				channelCredentials = {
					apiKey: formValues.apiKey,
					partnerId: formValues.partnerId,
				};
			}

			if (
				formValues.channelType.label === channelType.buybuyBaby ||
				formValues.channelType.label === channelType.mattressFirm ||
				formValues.channelType.label === channelType.homeDepot ||
				formValues.channelType.label === channelType.overstock ||
				formValues.channelType.label === channelType.wayfair ||
				formValues.channelType.label === channelType.overstockCA
			) {
				channelCredentials = {
					apiKey: formValues.apiKey,
					partnerId: formValues.partnerId,
				};
			}

			let customLabelValues = null;
			// If data on custom label modal was changed
			if (this.customLabel) {
				customLabelValues = { ...this.customLabel };
			} else {
				if (this.isEdit) {
					customLabelValues = {
						sellerName: this.channel.sellerName,
						sellerPhone: this.channel.sellerPhone,
						useCustomReturnAddress: this.channel.useCustomReturnAddress,
						returnName: this.channel.returnName,
						returnPhone: this.channel.returnPhone,
						returnAddress: this.channel.returnAddress,
						returnCity: this.channel.returnCity,
						returnZip: this.channel.returnZip,
						returnState: this.channel.returnState,
						returnStateName: this.channel.returnStateName,
					};
				} else {
					// Custom Label data on insert is required
					this.rootStore.notificationStore.error('MESSAGES.CHANNEL.MISSINGREQUIREDVALUESLABEL');
					this.resume();
					return;
				}
			}

			if (this.isEdit) {
				await this.listStore.updateChannel(
					{
						id: this.rootStore.routerStore.routerState.params.id,
						name: formValues.name,
						timezoneId: formValues.timezone.value,
						channelTypeId: formValues.channelType.value,
						carrierChannels: carrierChannels,
						shouldSync: this.channel.shouldSync,
						updateInventory: this.channel.updateInventory,
						shouldAcknowledgeOrders: formValues.shouldAcknowledgeOrders,
						shouldAutoCancel: formValues.shouldAutoCancel,
						useBuyShippingForPrime: formValues.useBuyShippingForPrime,
						useBuyShippingForNonPrime: formValues.useBuyShippingForNonPrime,
						useAmazonMli: formValues.useAmazonMli,
						checkBuyShippingSunday:
							this.channel.channelType.name === channelType.amazon ? formValues.checkBuyShippingSunday : false,
						sellerId: this.channel.sellerId,
						...channelCredentials,
						...customLabelValues,
					},
					sourceOfChange.updateChannel
				);
				this.rootStore.notificationStore.success(['LOOKUPUPDATE.CHANNEL', { name: this.form.$('name').value }]);
			} else {
				await this.listStore.createChannel(
					{
						name: formValues.name,
						channelTypeId: formValues.channelType.value,
						carrierChannels: carrierChannels,
						convertWarehouses: this.convertWarehouses,
						timezoneId: formValues.timezone.value,
						shouldAcknowledgeOrders: formValues.shouldAcknowledgeOrders,
						shouldAutoCancel: formValues.shouldAutoCancel,
						useBuyShippingForPrime: formValues.useBuyShippingForPrime,
						useBuyShippingForNonPrime: formValues.useBuyShippingForNonPrime,
						useAmazonMli: formValues.useAmazonMli,
						checkBuyShippingSunday:
							formValues.channelType.label === channelType.amazon ? formValues.checkBuyShippingSunday : false,
						sellerId: this.isSuperAdmin ? formValues.seller.value : null,
						sellerName: formValues.sellerName,
						sellerPhone: formValues.sellerPhone,
						...channelCredentials,
						...customLabelValues,
					},
					sourceOfChange.createChannel
				);
				this.rootStore.notificationStore.success(['LOOKUPCREATE.CHANNEL', { name: this.form.$('name').value }]);
			}

			this.rootStore.routerStore.goTo('master.app.main.channel.list');
		} catch (err) {
			if (err.statusCode === 500) {
				if (err.data.details === 'MESSAGES.CHANNEL.DUPLICATECREDENTIALS') {
					const errorMessage = translate(['MESSAGES.CHANNEL.DUPLICATECREDENTIALS']);
					this.rootStore.notificationStore.error(errorMessage);
				}
			} else {
				if (err.statusCode === 400) {
					err.data.errorMessage = translate('MESSAGES.CHANNEL.EXISTS');
					if (err.data.exceptionMessage === 'MESSAGES.CHANNEL.DUPLICATECREDENTIALS') {
						err.data.errorMessage = translate('MESSAGES.CHANNEL.DUPLICATECREDENTIALS');
					}
				}
				this.rootStore.notificationStore.error(err.data.errorMessage);
			}
		}
		this.resume();
	};

	@action.bound
	async getLookups(shouldGetChannelType = true) {
		await this.lookupStore.find({ page: 1, rpp: 15 }, lookupRouteName.timezone);
		this.timezoneOptions = _.map(this.lookupStore.data, item => {
			return { value: item.id, label: item.name, extra: item };
		});
		await this.lookupStore.find({ page: 1, rpp: 100 }, lookupRouteName.state);
		this.stateOptions = _.map(this.lookupStore.data, item => {
			return { label: item.name, value: item.countryStateCode, extra: item };
		});
		await this.lookupStore.find({ page: 1, rpp: 100 }, lookupRouteName.carrierType);
		this.carrierTypes = this.lookupStore.data;
		if (shouldGetChannelType) {
			await this.lookupStore.find({ page: 1, rpp: 100 }, lookupRouteName.channelType);
			this.channelTypeOptions = _.map(this.lookupStore.data, item => {
				return { value: item.id, label: item.name, extra: item };
			});
		}
	}

	@action.bound
	async handleOnTimezoneMenuScroll() {
		if (this.timezoneOptions.length < this.page * 15) return;
		this.isTimezoneLoading = true;
		this.page++;
		await this.lookupStore.find(
			{ search: this.infiniteSearchPhrase, page: this.page, rpp: 20 },
			lookupRouteName.timezone
		);
		this.tempTimezoneOptions = _.map(this.lookupStore.data, item => {
			return { value: item.id, label: item.name, extra: item };
		});
		if (this.tempTimezoneOptions.length > 0) {
			this.timezoneOptions = this.timezoneOptions.concat(this.tempTimezoneOptions);
		}
		this.isTimezoneLoading = false;
	}

	@action.bound
	async onMenuClose() {
		this.page = 1;
		this.infiniteSearchPhrase = null;
		await this.getLookups(false);
	}

	@action.bound
	setSellingPartnerAPIRules() {
		this.form.$('amazonSellerId').set('rules', 'required');
		this.form.$('marketplaceId').set('rules', 'required');
		this.form.$('refreshToken').set('rules', 'required');
	}

	@action.bound
	setFieldRules(selectedOption) {
		this.resetFieldRules();
		if (selectedOption.label === channelType.amazonVendor || selectedOption.label === channelType.amazon) {
			this.setSellingPartnerAPIRules();
		}
		if (selectedOption.label === channelType.amazon) {
			this.addBuyShippingCarrierFields();
			if (this.isEdit) {
				this.updateBuyShippingCarrierFields(
					_.filter(this.channel.carrierChannels, item => {
						return item.carrierChannelType == carrierChannelTypeEnum.buyShipping;
					})
				);
			}
		} else {
			this.removeBuyShippingCarrierFields();
		}

		if (selectedOption.label === channelType.amazonVendor) {
			this.form.$('sellingPartyId').set('rules', 'required');
			this.form.$('carrierChannels').set('rules', '');
		} else {
			this.form.$('sellingPartyId').set('rules', '');
			this.form.$('carrierChannels').set('rules', 'required');
		}

		if (
			selectedOption.label === channelType.costco ||
			selectedOption.label === channelType.overstockCA ||
			selectedOption.label === channelType.logicBroker
		) {
			this.form.$('apiKey').set('rules', 'required');
			this.form.$('partnerId').set('rules', 'required');
		}
		if (selectedOption.label === channelType.overstockCA) {
			this.form.$('apiKey').set('rules', 'required');
			this.form.$('partnerId').set('rules', 'required');
		}
		if (selectedOption.label === channelType.mirakl) {
			this.form.$('url').set('rules', 'required');
			this.form.$('shopId').set('rules', 'required');
			this.form.$('apiKey').set('rules', 'required');
		}
		if (selectedOption.label === channelType.shopify) {
			this.form.$('url').set('rules', 'required');
			this.form.$('accessToken').set('rules', 'required');
		}
		if (selectedOption.label === channelType.sellerCloud) {
			this.form.$('serverId').set('rules', 'required');
			this.form.$('userName').set('rules', 'required');
			this.form.$('password').set('rules', 'required');
			this.form.$('channelId').set('rules', 'required');
		}
		if (
			selectedOption.label === channelType.buybuyBaby ||
			selectedOption.label === channelType.mattressFirm ||
			selectedOption.label === channelType.homeDepot ||
			selectedOption.label === channelType.overstock ||
			selectedOption.label === channelType.wayfair
		) {
			this.form.$('apiKey').set('rules', 'required');
			this.form.$('partnerId').set('rules', 'required');
		}
		if (selectedOption.label === channelType.walmart) {
			this.form.$('apiKey').set('rules', 'required');
			this.form.$('apiSecret').set('rules', 'required');
		}
	}

	@action.bound
	resetFieldRules() {
		this.form.$('apiKey').set('rules', '');
		this.form.$('sellingPartyId').set('rules', '');
		this.form.$('partnerId').set('rules', '');
		this.form.$('apiSecret').set('rules', '');
		this.form.$('refreshToken').set('rules', '');
		this.form.$('accessToken').set('rules', '');
		this.form.$('amazonSellerId').set('rules', '');
		this.form.$('marketplaceId').set('rules', '');
		this.form.$('url').set('rules', '');
		this.form.$('shopId').set('rules', '');
		this.form.$('serverId').set('rules', '');
		this.form.$('userName').set('rules', '');
		this.form.$('password').set('rules', '');
		this.form.$('channelId').set('rules', '');
	}

	@action.bound
	async searchTimezoneAsync(searchPhrase, { action }) {
		if (searchPhrase === '') {
			this.page = 1;
		}
		if (action === 'input-change') {
			this.isTimezoneLoading = true;
			this.infiniteSearchPhrase = searchPhrase;
			this.page = 1;
			this.timezoneOptions = [];
			let filter = {
				search: searchPhrase || null,
				pageNumber: 1,
				pageSize: 15,
				orderDirection: 'asc',
				searchFields: 'name',
			};
			await this.lookupStore.find(filter, lookupRouteName.timezone);
			const newOptions = [];
			_.forEach(this.lookupStore.data, item => {
				newOptions.push({ value: item.id, label: item.name, extra: item });
			});
			this.timezoneOptions = newOptions;
			this.isTimezoneLoading = false;
		}
	}

	@action.bound
	addBuyShippingCarrierFields = () => {
		_.each(this.carrierTypes, carrierType => {
			const fieldName = `${carrierType.abrv.toLowerCase()}BuyShippingCarrier`;
			this.form.add({
				name: fieldName,
				rules: '',
				label: `CHANNEL.BUYSHIPPING.${carrierType.abrv.toUpperCase()}`,
			});
		});
	};

	@action.bound
	updateBuyShippingCarrierFields = carrierChannels => {
		_.forEach(carrierChannels, carrierChannel => {
			const fieldName = `${carrierChannel.carrier.carrierType.abrv.toLowerCase()}BuyShippingCarrier`;
			if (this.form.has(fieldName)) {
				this.form.$(fieldName).set('value', { value: carrierChannel.carrier.id, label: carrierChannel.carrier.name });
			}
		});
	};

	@action.bound
	clearBuyShippingCarrierFields = () => {
		_.forEach(this.carrierTypes, carrierType => {
			const fieldName = `${carrierType.abrv.toLowerCase()}BuyShippingCarrier`;
			if (this.form.has(fieldName)) {
				this.form.$(fieldName).reset();
			}
		});
	};

	@action.bound
	removeBuyShippingCarrierFields = () => {
		_.forEach(this.carrierTypes, carrierType => {
			const fieldName = `${carrierType.abrv.toLowerCase()}BuyShippingCarrier`;
			if (this.form.has(fieldName)) {
				this.form.del(fieldName);
			}
		});
	};

	@action.bound
	removeBuyShippingCarrier = () => {
		_.forEach(this.carrierTypes, carrierType => {
			const fieldName = `${carrierType.abrv.toLowerCase()}BuyShippingCarrier`;
			if (this.form.has(fieldName)) {
				if (
					_.filter(this.selectedCarriers, selectedCarrier => {
						return selectedCarrier.value == this.form.$(fieldName).value.value;
					}).length == 0
				) {
					this.form.$(fieldName).reset();
				}
			}
		});
	};

	@action.bound
	onSelectChange = (selectedOption, field, isChannelTypeSelect) => {
		field.value = selectedOption;
		if (isChannelTypeSelect) {
			this.setFieldRules(selectedOption);
		}
		if (this.form.$('channelType') && this.form.$('channelType').value.label === channelType.amazon) {
			this.form.$('useBuyShippingForPrime').value = true;
		} else {
			this.form.$('useBuyShippingForPrime').value = false;
		}
	};

	@action.bound
	async onAuthorizeAmazon() {
		try {
			await this.listStore.authorizeAmazon(this.oAuthDate);
			this.authorizedOAuthConfirmModal.open({
				message: ['OAUTH.AMAZON.CONFIRMSUCCESSAUTH'],
			});
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
	}

	authorizedOAuthConfirmModal = new ConfirmModalParams({
		onConfirm: async () => {
			// Get authorization results from Redis.
			try {
				var response = await this.listStore.getAuthorizationResultAmazon(this.amazonOAuthStateHash);
				if (isSome(response) && isSome(response.refreshToken) && isSome(response.sellerId)) {
					this.form.$('refreshToken').value = response.refreshToken;
					this.form.$('amazonSellerId').value = response.sellerId;
					this.rootStore.notificationStore.success(['OAUTH.AMAZON.SUCCESSFORMUPDATE']);
				} else {
					this.rootStore.notificationStore.error(['OAUTH.AMAZON.FAILGETREFRESHTOKEN']);
				}
			} catch (err) {
				if (err.statusCode === 404) {
					this.rootStore.notificationStore.error(['OAUTH.AMAZON.STATENOTFOUND']);
				} else {
					notificationError({ store: this.rootStore.notificationStore, err });
				}
			}
		},
	});

	editRestockDateModal = new ModalParams();

	openEditRestockDateModal = () => {
		this.selectedItem = this.channel;
		this.editRestockDateModal.open();
	};

	closeEditRestockDateModal = () => {
		this.selectedItem = this.channel;
		this.editRestockDateModal.close();
	};

	openEditChannelWarehouseCodesModal = () => {
		if (this.isSuperAdmin && this.sellerId == null) {
			this.rootStore.notificationStore.warning(['CHANNEL.ORGANIZATIONREQUIRED']);
			return;
		}
		this.selectedItem = this.channel;
		this.editChannelWarehouseCodesModal.open();
	};

	closeEditChannelWarehouseCodesModal = () => {
		this.editChannelWarehouseCodesModal.close();
	};

	openCustomLabelModal = () => {
		this.selectedItem = this.channel;
		this.customLabelModal.open();
	};

	closeCustomLabelModal = () => {
		this.customLabelModal.close();
	};

	openUpdateInventoryLogModal = () => {
		this.updateInventoryLogModal.open();
	};

	closeUpdateInventoryLogModal = () => {
		this.updateInventoryLogModal.close();
	};

	editChannelWarehouseCodesModal = new ModalParams();
	updateInventoryLogModal = new ModalParams();
	customLabelModal = new ModalParams();
}

export default ChannelEditViewStore;



// WEBPACK FOOTER //
// ./src/modules/channel/stores/ChannelEditViewStore.js